import React, { useState } from 'react'
import Markdown from 'react-markdown'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import style from '@styles/account/UpdateAccountForm.module.scss'
import { MIN_PASSWORD_LENGTH } from '@lib/toolkitApi'
import { AccountForm } from '@components/account/AccountForm'
import { PasswordField } from '@components/form/PasswordField'
import { SubmitButton } from '@components/form/SubmitButton'
import { UserType } from '@queries/types/user'
import { getFieldErrorsMessage } from '@lib/toolkitApi'
import { resetPasswordQuery as apiResetPasswordQuery } from '@queries/resetPasswordQuery'
import { updateAccountQuery as apiUpdateAccountQuery } from '@queries/updateAccountQuery'

interface Props {
  user: UserType
  resetPasswordQuery?: typeof apiResetPasswordQuery
  updateAccountQuery?: typeof apiUpdateAccountQuery
}

const UpdateAccountForm = ({
  user,
  resetPasswordQuery = apiResetPasswordQuery,
  updateAccountQuery = apiUpdateAccountQuery,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showPasswordFields, setShowPasswordFields] = useState<boolean>(false)
  const { t, i18n } = useTranslation()

  const useFormMethods = useForm()
  const { handleSubmit, register } = useFormMethods

  const onSubmit = async (data: { locale: string; token: string }) => {
    try {
      setErrorMessage(null)
      setSuccessMessage(null)

      data.locale = i18n.language
      data.token = user.token

      await updateAccountQuery(data)

      setShowPasswordFields(false)
      setSuccessMessage(t('account.updateAccount.successMessage'))
    } catch (error) {
      const fieldErrorsMessage = getFieldErrorsMessage(error)
      const newErrorMessage = `${t('account.updateAccount.errorMessage')}\n\n${fieldErrorsMessage}`
      setErrorMessage(newErrorMessage)
    }
  }

  const requiredErrorMessage = t('form.requiredErrorMessage')

  const onForgotPassword = async (email: string) => {
    const data = { email: email, locale: i18n.language }

    try {
      setErrorMessage(null)
      setSuccessMessage(null)

      await resetPasswordQuery(data)

      setShowPasswordFields(false)
      setSuccessMessage(t('account.updateAccount.forgotPasswordMessage', { email: email }))
    } catch (error) {
      const fieldErrorsMessage = getFieldErrorsMessage(error)
      const newErrorMessage = `${t('account.updateAccount.errorMessage')}\n\n${fieldErrorsMessage}`
      setErrorMessage(newErrorMessage)
    }
  }

  return (
    <form className={style.form} onSubmit={handleSubmit(onSubmit)}>
      {errorMessage && (
        <Markdown className={style.errorMessage} linkTarget="_blank" source={errorMessage} />
      )}
      {successMessage && <div className={style.successMessage}>{successMessage}</div>}
      <FormProvider {...useFormMethods}>
        <AccountForm user={user} />
        {showPasswordFields && (
          <>
            <div className={style.currentPasswordContainer}>
              <PasswordField
                errorMessages={{ required: requiredErrorMessage }}
                inputId="current_password"
                labelText={t('account.form.currentPassword')}
                placeholder={t('account.form.currentPassword')}
                ref={register({ required: true })}
              />
            </div>
            <PasswordField
              errorMessages={{
                required: t('form.requiredErrorMessage'),
                minLength: t('form.minLengthErrorMessage', { count: MIN_PASSWORD_LENGTH }),
              }}
              inputId="new_password"
              labelText={t('account.form.newPassword')}
              placeholder={t('account.form.newPassword')}
              ref={register({ required: true, minLength: MIN_PASSWORD_LENGTH })}
            />
          </>
        )}
        {showPasswordFields ? (
          <div className={style.updatePasswordButtonsContainer}>
            <button
              className={style.cancelButton}
              type="button"
              onClick={() => setShowPasswordFields(false)}
            >
              {t('account.updateAccount.cancelButton')}
            </button>
            <button
              className={style.forgotPasswordButton}
              type="button"
              onClick={() => onForgotPassword(user.email)}
            >
              {t('account.updateAccount.forgotPassword')}
            </button>
          </div>
        ) : (
          <button
            className={style.changePasswordButton}
            type="button"
            onClick={() => setShowPasswordFields(true)}
          >
            {t('account.updateAccount.changePassword')}
          </button>
        )}
      </FormProvider>
      <div className={style.buttonContainer}>
        <SubmitButton text={t('account.updateAccount.submitButton')} />
      </div>
    </form>
  )
}

export { UpdateAccountForm }
